import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { GlassColorPalette, GlassInputControl, GlassInputGroup, GlassInputGroupEscape, GlassInputGroupTitle, GlassTextInputTransformer } from "src/components/GlassInput";
import { ConfirmationDialog } from "src/components/ConfirmationDialog";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { PLAProject, PLAUserAccessRights, PLAAttributeDefinition, PLAElementTypeDefinition, PLAAlertDefinition, PLARelationTypeDefinition, PLAValueType, getNextPLAElementTypeColor, getPLAMainHierarchicalRelationTypeId, withDefaultPLAMainHierarchicalRelationType } from "./PLAState";
import { migratePLACubeTotalsToElementTypes } from "./PLACubeTimeView";
import { validatePLAExpression } from "./PLAExpression";
import { collectPLAProjectExpressionIssues, formatPLAProjectExpressionIssue, PLAProjectExpressionIssue } from "./PLAProjectValidation";
import { getSortedPLAAttributeEntries } from "./PLAAttributeDefinitions";
import { PLAExpressionSimulationDialog } from "./PLAExpressionSimulationDialog";

type EditElementTypeState = { originalId?: string; draftId: string; value: PLAElementTypeDefinition };
type EditAttributeState = { elementTypeId: string; originalId?: string; draftId: string; value: PLAAttributeDefinition };
type EditAlertState = { elementTypeId: string; attributeId: string; originalId?: string; draftId: string; value: PLAAlertDefinition };
type EditRelationTypeState = { originalId?: string; draftId: string; value: PLARelationTypeDefinition };
interface SubFormEntry { name: string; value: string }
type DeleteState =
   | { kind: "elementType"; elementTypeId: string }
   | { kind: "attribute"; elementTypeId: string; attributeId: string }
   | { kind: "alert"; elementTypeId: string; attributeId: string; alertId: string }
   | { kind: "relationType"; relationTypeId: string };

const overlayRoot = () => document.getElementById("Mit-NonBlockingOverlay") || document.body;
const expressionIssueRowStyle: React.CSSProperties = { background: "rgba(231, 76, 60, 0.14)", boxShadow: "inset 3px 0 0 #e74c3c" };

function updateRecord<T>(record: Record<string, T>, key: string, value: T) {
   return { ...record, [key]: value };
}

function removeRecord<T>(record: Record<string, T>, key: string) {
   const next = { ...record };
   delete next[key];
   return next;
}

function renameRecord<T>(record: Record<string, T>, originalId: string | undefined, nextId: string, value: T) {
   const next = originalId ? removeRecord(record, originalId) : { ...record };
   next[nextId] = value;
   return next;
}

function nextUniqueId(prefix: string, record: Record<string, unknown>) {
   let id = crypto.randomUUID?.() || `${prefix}${Date.now()}`;
   while (record[id]) id = crypto.randomUUID?.() || `${prefix}${Date.now()}`;
   return id;
}

function ActionIconButton(props: { title: string; icon: "add" | "edit" | "delete"; onClick: () => void; disabled?: boolean }) {
   return (
      <GlassButton
         title={props.title}
         aria-label={props.title}
         disabled={props.disabled}
         onClick={props.onClick}
         style={{ width: 30, minWidth: 30, height: 28, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
      >
         <MIBIcon icon={props.icon} />
      </GlassButton>
   );
}

function TinyTable(props: { children: React.ReactNode }) {
   return <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>{props.children}</table>;
}

function HeaderCell(props: { children: React.ReactNode }) {
   return <th style={{ textAlign: "left", borderBottom: "1px solid rgba(255,255,255,.25)", padding: "4px 6px" }}>{props.children}</th>;
}

function Cell(props: { children: React.ReactNode; align?: "left" | "right" }) {
   return <td style={{ padding: "4px 6px", verticalAlign: "top", textAlign: props.align || "left" }}>{props.children}</td>;
}

function SectionHeaderRow(props: { title: string; children?: React.ReactNode }) {
   return (
      <div style={{ gridColumn: "1 / 3", width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 8, marginBottom: 4 }}>
         <GlassInputGroupTitle>{props.title}</GlassInputGroupTitle>
         {props.children ? <GlassButtonRow adjust="end">{props.children}</GlassButtonRow> : null}
      </div>
   );
}

function expressionIssueTitle(project: PLAProject, issues: PLAProjectExpressionIssue[]) {
   return issues.map((issue) => formatPLAProjectExpressionIssue(project, issue)).join("\n");
}

function expressionIssueRowProps(project: PLAProject, issues: PLAProjectExpressionIssue[]) {
   return issues.length ? { style: expressionIssueRowStyle, title: expressionIssueTitle(project, issues) } : {};
}

function AccessRightsSubForm(props: { subFormData: SubFormEntry[]; setSubFormData: (value: SubFormEntry[]) => void }) {
   const addSubFormEntry = () => {
      props.setSubFormData([...props.subFormData, { name: "", value: "" }]);
   };

   const removeSubFormEntry = (index: number) => {
      const updatedSubforms = [...props.subFormData];
      updatedSubforms.splice(index, 1);
      props.setSubFormData(updatedSubforms);
   };

   const handleSubFormChange = (index: number, field: "name" | "value", event: React.ChangeEvent<any>) => {
      const { value } = event.target;
      const updatedSubforms = props.subFormData.map((item, idx) => {
         return idx === index ? field === "name" ? { name: value, value: item.value } : { name: item.name, value } : item;
      });
      props.setSubFormData(updatedSubforms);
   };

   return (
      <GlassInputGroupEscape>
         {props.subFormData.map((subform, index) => (
            <div key={index} className="GlassInputField" style={{ marginBottom: "5px" }}>
               <GlassInputControl>
                  <input type="text" value={subform.name} onChange={(event) => handleSubFormChange(index, "name", event)} placeholder={Localization.getText("Email")} autoComplete="off" />
                  <select
                     style={{ flex: "auto 1 0" }}
                     name="product"
                     value={subform.value || ""}
                     onChange={(event) => handleSubFormChange(index, "value", event)}
                  >
                     {subform.value === "" && <option value="">{Localization.getText("Select a access")}</option>}
                     {[PLAUserAccessRights.Read, PLAUserAccessRights.Write, PLAUserAccessRights.Admin].map((accessRight: string) => {
                        return <option key={accessRight} value={accessRight}>{accessRight}</option>;
                     })}
                  </select>
               </GlassInputControl>
               <div className="Setting_Butto" title={Localization.getText("Delete user")}>
                  <span onClick={() => removeSubFormEntry(index)}>
                     <MIBIcon icon="delete" />
                  </span>
               </div>
            </div>
         ))}
         <GlassButton style={{ marginLeft: "auto" }} onClick={addSubFormEntry}>{Localization.getText("Add Entry")}</GlassButton>
      </GlassInputGroupEscape>
   );
}

function transformAccessRights(data: Record<string, string[]>): SubFormEntry[] {
   return data ? Object.keys(data).map((key) => {
      return data[key]?.map?.((accessRight) => {
         return { name: key, value: accessRight };
      }) || [{ name: key, value: "" }];
   }).flat() : [];
}

function accessRightsFromSubForm(value: SubFormEntry[]): Record<string, string[]> {
   const result: Record<string, string[]> = {};
   value.forEach((entry) => {
      if (result[entry.name]) {
         result[entry.name].push(entry.value);
      } else {
         result[entry.name] = [entry.value];
      }
   });
   return result;
}

function ExpressionError(props: { expression: string; project?: PLAProject; elementType?: PLAElementTypeDefinition }) {
   const validation = validatePLAExpression(props.expression || "", { project: props.project, elementType: props.elementType });
   if (!validation.valid) return <div style={{ color: "#e74c3c", marginTop: 4 }}>{validation.error}</div>;
   if (validation.warnings?.length) return <div style={{ color: "#b36b00", marginTop: 4 }}>{validation.warnings.join("\n")}</div>;
   return null;
}

function ElementTypeEditor(props: { project: PLAProject; state: EditElementTypeState; onClose: () => void; onSave: (state: EditElementTypeState) => void }) {
   const [state, setState] = useState(props.state);
   const [editAttribute, setEditAttribute] = useState<EditAttributeState | undefined>();
   const [deleteAttributeId, setDeleteAttributeId] = useState<string | undefined>();
   const attributes = state.value.attributes || {};
   const sortedAttributes = useMemo(() => getSortedPLAAttributeEntries(attributes), [attributes]);
   const validationProject = useMemo(() => ({ ...props.project, elementTypes: renameRecord(props.project.elementTypes, state.originalId, state.draftId.trim() || state.draftId, state.value) }), [props.project, state]);
   const expressionIssues = useMemo(() => collectPLAProjectExpressionIssues(validationProject).filter((issue) => issue.elementTypeId === (state.draftId.trim() || state.draftId)), [validationProject, state.draftId]);
   const colorSet: string[] = SettingsManager.getSystemSetting("standardSingleColorRange", [], true) || [];
   const defaultColor = state.value.color || colorSet[0] || "#2d8f85";
   const addAttribute = () => {
      const id = nextUniqueId("attribute", attributes);
      setEditAttribute({ elementTypeId: state.draftId, draftId: id, value: { name: id, type: "constant", valueType: "string", sortOrder: Object.keys(attributes).length + 1, alerts: {} } });
   };
   const saveAttributeLocal = (attributeState: EditAttributeState) => {
      setState((prev) => ({
         ...prev,
         value: {
            ...prev.value,
            attributes: renameRecord(prev.value.attributes || {}, attributeState.originalId, attributeState.draftId.trim(), attributeState.value)
         }
      }));
      setEditAttribute(undefined);
   };
   const deleteAttributeLocal = () => {
      if (!deleteAttributeId) return;
      setState((prev) => ({
         ...prev,
         value: { ...prev.value, attributes: removeRecord(prev.value.attributes || {}, deleteAttributeId) }
      }));
      setDeleteAttributeId(undefined);
   };

   return (
      <>
         <AdvancedDragModal title={Localization.getText("Element type details")} PosDefault={{ width: "820px", left: "50%", transform: "translateX(-50%)", top: "90px" }} onClose={props.onClose}>
            <ADModalBody>
               <GlassInputGroup onSubmit={() => props.onSave(state)} onEscape={props.onClose}>
                  <GlassTextInputTransformer label={Localization.getText("Name")}><input value={state.value.name} onChange={(event) => setState({ ...state, value: { ...state.value, name: event.target.value } })} /></GlassTextInputTransformer>
                  <GlassTextInputTransformer label={Localization.getText("Sort order")}><input type="number" value={state.value.sortOrder} onChange={(event) => setState({ ...state, value: { ...state.value, sortOrder: Number(event.target.value) } })} /></GlassTextInputTransformer>
                  <GlassTextInputTransformer label={Localization.getText("Color")}>
                     <GlassColorPalette
                        defaultColor={defaultColor}
                        colorSet={colorSet.length ? colorSet : [defaultColor]}
                        getter={() => state.value.color || defaultColor}
                        onChange={(color: string) => setState({ ...state, value: { ...state.value, color } })}
                     />
                  </GlassTextInputTransformer>
                  <GlassTextInputTransformer label={Localization.getText("Cube totals")}>
                     <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, width: "100%" }}>
                        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                           <input type="checkbox" checked={state.value.total !== false} onChange={(event) => setState({ ...state, value: { ...state.value, total: event.target.checked } })} />
                           <span>{Localization.getText("Show cube totals")}</span>
                        </label>
                        <small>{Localization.getText("Cube totals across this element type hint")}</small>
                     </div>
                  </GlassTextInputTransformer>
                  <SectionHeaderRow title={Localization.getText("Attributes")}>
                     <GlassButton onClick={addAttribute}>{Localization.getText("Add attribute")}</GlassButton>
                  </SectionHeaderRow>
                  <TinyTable>
                     <thead><tr><HeaderCell>{Localization.getText("Name")}</HeaderCell><HeaderCell>{Localization.getText("Sort order")}</HeaderCell><HeaderCell>{Localization.getText("Type")}</HeaderCell><HeaderCell>{Localization.getText("Description")}</HeaderCell><HeaderCell>{Localization.getText("Alerts")}</HeaderCell><HeaderCell>{Localization.getText("Actions")}</HeaderCell></tr></thead>
                     <tbody>
                        {sortedAttributes.map(([attributeId, attribute]) => {
                           const attributeIssues = expressionIssues.filter((issue) => issue.attributeId === attributeId);
                           return <tr key={attributeId} {...expressionIssueRowProps(validationProject, attributeIssues)}>
                              <Cell>{attribute.name}</Cell>
                              <Cell>{attribute.sortOrder ?? ""}</Cell>
                              <Cell>{attribute.type === "constant" ? Localization.getText("Constant") : attribute.type === "cube" ? Localization.getText("Cube") : Localization.getText("Expression")}</Cell>
                              <Cell>{attribute.description || ""}</Cell>
                              <Cell>{Object.keys(attribute.alerts || {}).length}</Cell>
                              <Cell><GlassButtonRow><ActionIconButton title={Localization.getText("Edit attribute")} icon="edit" onClick={() => setEditAttribute({ elementTypeId: state.draftId, originalId: attributeId, draftId: attributeId, value: attribute })} /><ActionIconButton title={Localization.getText("Delete attribute")} icon="delete" onClick={() => setDeleteAttributeId(attributeId)} /></GlassButtonRow></Cell>
                           </tr>;
                        })}
                     </tbody>
                  </TinyTable>
               </GlassInputGroup>
            </ADModalBody>
            <ADModalFooter><GlassButtonRow><GlassButton disabled={!state.draftId.trim()} onClick={() => props.onSave(state)}>{Localization.getText("Save")}</GlassButton><GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton></GlassButtonRow></ADModalFooter>
         </AdvancedDragModal>
         {editAttribute ? createPortal(<AttributeEditor project={validationProject} elementType={state.value} state={editAttribute} onClose={() => setEditAttribute(undefined)} onSave={saveAttributeLocal} />, overlayRoot()) : null}
         {deleteAttributeId ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Confirm deletion")} onSubmit={deleteAttributeLocal} onCancel={() => setDeleteAttributeId(undefined)}>
               {Localization.getText("Confirm deletion")}
            </ConfirmationDialog>,
            overlayRoot()
         ) : null}
      </>
   );
}

function AttributeEditor(props: { project: PLAProject; elementType: PLAElementTypeDefinition; state: EditAttributeState; onClose: () => void; onSave: (state: EditAttributeState) => void }) {
   const [state, setState] = useState(props.state);
   const [editAlert, setEditAlert] = useState<EditAlertState | undefined>();
   const [deleteAlertId, setDeleteAlertId] = useState<string | undefined>();
   const [editingExpression, setEditingExpression] = useState(false);
   const alerts = state.value.alerts || {};
   const updatedElementType = useMemo(() => ({ ...props.elementType, attributes: renameRecord(props.elementType.attributes || {}, state.originalId, state.draftId.trim() || state.draftId, state.value) }), [props.elementType, state]);
   const validationProject = useMemo(() => ({ ...props.project, elementTypes: updateRecord(props.project.elementTypes, state.elementTypeId, updatedElementType) }), [props.project, state.elementTypeId, updatedElementType]);
   const expressionIssues = useMemo(() => collectPLAProjectExpressionIssues(validationProject).filter((issue) => issue.elementTypeId === state.elementTypeId && issue.attributeId === (state.draftId.trim() || state.draftId)), [validationProject, state.elementTypeId, state.draftId]);
   const updateValue = (value: PLAAttributeDefinition) => setState({ ...state, value });
   const addAlert = () => {
      const id = nextUniqueId("alert", alerts);
      setEditAlert({ elementTypeId: state.elementTypeId, attributeId: state.draftId, draftId: id, value: { booleanExpression: "", alertMessage: "", optimizerMayOverride: false } });
   };
   const saveAlertLocal = (alertState: EditAlertState) => {
      updateValue({
         ...state.value,
         alerts: renameRecord(alerts, alertState.originalId, alertState.draftId.trim(), alertState.value)
      });
      setEditAlert(undefined);
   };
   const cubeDimensions = state.value.cubeDimensions || [];
   const elementTypeOptions = Object.entries(props.project.elementTypes || {}).sort((a, b) => (a[1].name || a[0]).localeCompare(b[1].name || b[0]));
   const addCubeDimension = () => {
      const firstUnusedElementTypeId = elementTypeOptions.find(([elementTypeId]) => !cubeDimensions.some((dimension) => dimension.elementTypeId === elementTypeId))?.[0];
      if (firstUnusedElementTypeId) updateValue({ ...state.value, cubeDimensions: [...cubeDimensions, { elementTypeId: firstUnusedElementTypeId }] });
   };
   const updateCubeDimension = (index: number, changes: Partial<{ elementTypeId: string; expanded: boolean }>) => {
      const nextDimensions = cubeDimensions.map((dimension, dimensionIndex) => dimensionIndex === index ? { ...dimension, ...changes } : dimension);
      if (nextDimensions.filter((dimension) => dimension.expanded).length > 2) return;
      updateValue({ ...state.value, cubeDimensions: nextDimensions });
   };
   const removeCubeDimension = (index: number) => {
      updateValue({ ...state.value, cubeDimensions: cubeDimensions.filter((_dimension, dimensionIndex) => dimensionIndex !== index) });
   };
   const deleteAlertLocal = () => {
      if (!deleteAlertId) return;
      updateValue({ ...state.value, alerts: removeRecord(alerts, deleteAlertId) });
      setDeleteAlertId(undefined);
   };

   return (
      <>
         <AdvancedDragModal title={Localization.getText("Attribute details")} PosDefault={{ width: "760px", left: "50%", transform: "translateX(-50%)", top: "80px" }} onClose={props.onClose}>
            <ADModalBody>
               <GlassInputGroup onSubmit={() => props.onSave(state)} onEscape={props.onClose}>
                  <GlassTextInputTransformer label={Localization.getText("Name")}><input value={state.value.name} onChange={(event) => updateValue({ ...state.value, name: event.target.value })} /></GlassTextInputTransformer>
                  <GlassTextInputTransformer label={Localization.getText("Description")}><textarea rows={3} style={{ width: "100%", resize: "vertical" }} value={state.value.description || ""} onChange={(event) => updateValue({ ...state.value, description: event.target.value })} /></GlassTextInputTransformer>
                  <GlassTextInputTransformer label={Localization.getText("Sort order")}><input type="number" value={state.value.sortOrder ?? ""} onChange={(event) => updateValue({ ...state.value, sortOrder: event.target.value === "" ? undefined : Number(event.target.value) })} /></GlassTextInputTransformer>
                  <GlassTextInputTransformer label={Localization.getText("Type")}>
                     <select value={state.value.type} onChange={(event) => updateValue({ ...state.value, type: event.target.value as "constant" | "expression" | "cube" })}>
                        <option value="constant">{Localization.getText("Constant")}</option>
                        <option value="expression">{Localization.getText("Expression")}</option>
                        <option value="cube">{Localization.getText("Cube")}</option>
                     </select>
                  </GlassTextInputTransformer>
                  <GlassTextInputTransformer label={Localization.getText("Value type")}>
                     <select value={state.value.valueType || "string"} onChange={(event) => updateValue({ ...state.value, valueType: event.target.value as PLAValueType })}>
                        <option value="string">{Localization.getText("String")}</option>
                        <option value="number">{Localization.getText("Number")}</option>
                        <option value="m2">m2</option>
                        <option value="stk">stk</option>
                        <option value="kr">kr</option>
                        <option value="date">{Localization.getText("Date")}</option>
                        <option value="boolean">{Localization.getText("Boolean")}</option>
                     </select>
                  </GlassTextInputTransformer>
                  <GlassTextInputTransformer label={Localization.getText("Hide as default")}>
                     <input type="checkbox" checked={Boolean(state.value.hideAsDefault)} onChange={(event) => updateValue({ ...state.value, hideAsDefault: event.target.checked })} />
                  </GlassTextInputTransformer>
                  {state.value.type === "expression" && <GlassTextInputTransformer label={Localization.getText("Expression")}>
                     <div style={{ width: "100%" }}>
                        <GlassInputControl>
                           <textarea rows={2} style={{ width: "100%", resize: "vertical" }} readOnly value={state.value.expression || ""} />
                           <GlassButton onClick={() => setEditingExpression(true)}>{Localization.getText("Edit expression")}</GlassButton>
                        </GlassInputControl>
                        <ExpressionError expression={state.value.expression || ""} project={validationProject} elementType={updatedElementType} />
                     </div>
                  </GlassTextInputTransformer>}
                  {state.value.type === "cube" && <GlassTextInputTransformer label={Localization.getText("Cube dimensions")}>
                     <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
                        <TinyTable>
                           <thead><tr><HeaderCell>{Localization.getText("Element type")}</HeaderCell><HeaderCell>{Localization.getText("Expanded")}</HeaderCell><HeaderCell>{Localization.getText("Actions")}</HeaderCell></tr></thead>
                           <tbody>
                              {cubeDimensions.map((dimension, index) => (
                                 <tr key={`${dimension.elementTypeId}-${index}`}>
                                    <Cell>
                                       <select value={dimension.elementTypeId} onChange={(event) => updateCubeDimension(index, { elementTypeId: event.target.value })}>
                                          {elementTypeOptions.map(([elementTypeId, elementType]) => <option key={elementTypeId} value={elementTypeId}>{elementType.name || elementTypeId}</option>)}
                                       </select>
                                    </Cell>
                                    <Cell>
                                       <input type="checkbox" checked={Boolean(dimension.expanded)} onChange={(event) => updateCubeDimension(index, { expanded: event.target.checked })} />
                                    </Cell>
                                    <Cell><ActionIconButton title={Localization.getText("Delete cube dimension")} icon="delete" onClick={() => removeCubeDimension(index)} /></Cell>
                                 </tr>
                              ))}
                           </tbody>
                        </TinyTable>
                        <GlassButton disabled={!elementTypeOptions.some(([elementTypeId]) => !cubeDimensions.some((dimension) => dimension.elementTypeId === elementTypeId))} onClick={addCubeDimension}>{Localization.getText("Add cube dimension")}</GlassButton>
                        <small>{Localization.getText("Maximum two cube dimensions can be expanded. Use cube total for values that are not broken down by a dimension.")}</small>
                     </div>
                  </GlassTextInputTransformer>}
                  <SectionHeaderRow title={Localization.getText("Alerts")}>
                     <GlassButton onClick={addAlert}>{Localization.getText("Add alert")}</GlassButton>
                  </SectionHeaderRow>
                  <TinyTable>
                     <thead><tr><HeaderCell>{Localization.getText("Expression")}</HeaderCell><HeaderCell>{Localization.getText("Message")}</HeaderCell><HeaderCell>{Localization.getText("Optimizer may override")}</HeaderCell><HeaderCell>{Localization.getText("Actions")}</HeaderCell></tr></thead>
                     <tbody>
                        {Object.entries(alerts).map(([alertId, alert]) => {
                           const alertIssues = expressionIssues.filter((issue) => issue.alertId === alertId);
                           return <tr key={alertId} {...expressionIssueRowProps(validationProject, alertIssues)}>
                              <Cell>{alert.booleanExpression}</Cell>
                              <Cell>{alert.alertMessage}</Cell>
                              <Cell>{alert.optimizerMayOverride ? Localization.getText("yes") : ""}</Cell>
                              <Cell><GlassButtonRow><ActionIconButton title={Localization.getText("Edit alert")} icon="edit" onClick={() => setEditAlert({ elementTypeId: state.elementTypeId, attributeId: state.draftId, originalId: alertId, draftId: alertId, value: alert })} /><ActionIconButton title={Localization.getText("Delete alert")} icon="delete" onClick={() => setDeleteAlertId(alertId)} /></GlassButtonRow></Cell>
                           </tr>;
                        })}
                     </tbody>
                  </TinyTable>
               </GlassInputGroup>
            </ADModalBody>
            <ADModalFooter><GlassButtonRow><GlassButton disabled={!state.draftId.trim()} onClick={() => props.onSave(state)}>{Localization.getText("Save")}</GlassButton><GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton></GlassButtonRow></ADModalFooter>
         </AdvancedDragModal>
         {editingExpression ? <PLAExpressionSimulationDialog project={validationProject} elementTypeId={state.elementTypeId} initialExpression={state.value.expression || ""} portalRoot={overlayRoot()} onClose={() => setEditingExpression(false)} onApply={(expression) => { updateValue({ ...state.value, expression }); setEditingExpression(false); }} /> : null}
         {editAlert ? createPortal(<AlertEditor project={validationProject} elementType={updatedElementType} state={editAlert} onClose={() => setEditAlert(undefined)} onSave={saveAlertLocal} />, overlayRoot()) : null}
         {deleteAlertId ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Confirm deletion")} onSubmit={deleteAlertLocal} onCancel={() => setDeleteAlertId(undefined)}>
               {Localization.getText("Confirm deletion")}
            </ConfirmationDialog>,
            overlayRoot()
         ) : null}
      </>
   );
}

function AlertEditor(props: { project: PLAProject; elementType: PLAElementTypeDefinition; state: EditAlertState; onClose: () => void; onSave: (state: EditAlertState) => void }) {
   const [state, setState] = useState(props.state);
   const [editingExpression, setEditingExpression] = useState(false);
   return (
      <AdvancedDragModal title={Localization.getText("Alert details")} PosDefault={{ width: "640px", left: "50%", transform: "translateX(-50%)", top: "100px" }} onClose={props.onClose}>
         <ADModalBody>
            <GlassInputGroup onSubmit={() => props.onSave(state)} onEscape={props.onClose}>
               <GlassTextInputTransformer label={Localization.getText("Expression")}>
                  <div style={{ width: "100%" }}>
                     <GlassInputControl><textarea rows={4} style={{ width: "100%", resize: "vertical" }} readOnly value={state.value.booleanExpression} /><GlassButton onClick={() => setEditingExpression(true)}>{Localization.getText("Edit expression")}</GlassButton></GlassInputControl>
                     <ExpressionError expression={state.value.booleanExpression} project={props.project} elementType={props.elementType} />
                  </div>
               </GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Message")}><input value={state.value.alertMessage} onChange={(event) => setState({ ...state, value: { ...state.value, alertMessage: event.target.value } })} /></GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Optimizer may override")}><input type="checkbox" checked={Boolean(state.value.optimizerMayOverride)} onChange={(event) => setState({ ...state, value: { ...state.value, optimizerMayOverride: event.target.checked } })} /></GlassTextInputTransformer>
            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter><GlassButtonRow><GlassButton disabled={!state.draftId.trim()} onClick={() => props.onSave(state)}>{Localization.getText("Save")}</GlassButton><GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton></GlassButtonRow></ADModalFooter>
         {editingExpression ? <PLAExpressionSimulationDialog project={props.project} elementTypeId={state.elementTypeId} initialExpression={state.value.booleanExpression} portalRoot={overlayRoot()} onClose={() => setEditingExpression(false)} onApply={(expression) => { setState({ ...state, value: { ...state.value, booleanExpression: expression } }); setEditingExpression(false); }} /> : null}
      </AdvancedDragModal>
   );
}

function RelationTypeEditor(props: { state: EditRelationTypeState; onClose: () => void; onSave: (state: EditRelationTypeState) => void }) {
   const [state, setState] = useState(props.state);
   return (
      <AdvancedDragModal title={Localization.getText("Relation type details")} PosDefault={{ width: "480px", left: "50%", transform: "translateX(-50%)", top: "100px" }} onClose={props.onClose}>
         <ADModalBody>
            <GlassInputGroup onSubmit={() => props.onSave(state)} onEscape={props.onClose}>
               <GlassTextInputTransformer label={Localization.getText("Name")}><input value={state.value.name} onChange={(event) => setState({ ...state, value: { name: event.target.value } })} /></GlassTextInputTransformer>
            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter><GlassButtonRow><GlassButton disabled={!state.draftId.trim()} onClick={() => props.onSave(state)}>{Localization.getText("Save")}</GlassButton><GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton></GlassButtonRow></ADModalFooter>
      </AdvancedDragModal>
   );
}

export function PLAProjectEditor(props: { title?: string; value: PLAProject; onClose: () => void; onChange: (project: PLAProject) => void }) {
   const [project, setProject] = useState<PLAProject>(() => migratePLACubeTotalsToElementTypes(withDefaultPLAMainHierarchicalRelationType(props.value)));
   const [editElementType, setEditElementType] = useState<EditElementTypeState | undefined>();
   const [editRelationType, setEditRelationType] = useState<EditRelationTypeState | undefined>();
   const [deleteState, setDeleteState] = useState<DeleteState | undefined>();

   const sortedElementTypes = useMemo(() => Object.entries(project.elementTypes).sort((a, b) => a[1].sortOrder - b[1].sortOrder), [project.elementTypes]);
   const sortedRelationTypes = useMemo(() => Object.entries(project.relationTypes).sort((a, b) => a[1].name.localeCompare(b[1].name)), [project.relationTypes]);
   const expressionIssues = useMemo(() => collectPLAProjectExpressionIssues(project), [project]);
   const selectedMainHierarchicalRelationTypeId = getPLAMainHierarchicalRelationTypeId(project) || "";

   const openNewElementType = () => {
      const id = nextUniqueId("elementType", project.elementTypes);
      setEditElementType({ draftId: id, value: { name: id, sortOrder: Object.keys(project.elementTypes).length + 1, color: getNextPLAElementTypeColor(), attributes: {} } });
   };

   const openNewRelationType = () => {
      const id = nextUniqueId("relationType", project.relationTypes);
      setEditRelationType({ draftId: id, value: { name: id } });
   };

   const saveElementType = (state: EditElementTypeState) => {
      const nextId = state.draftId.trim();
      setProject((prev) => ({ ...prev, elementTypes: renameRecord(prev.elementTypes, state.originalId, nextId, { ...state.value, color: state.value.color || getNextPLAElementTypeColor() }) }));
      setEditElementType(undefined);
   };

   const saveRelationType = (state: EditRelationTypeState) => {
      const nextId = state.draftId.trim();
      setProject((prev) => ({
         ...prev,
         mainHierarchicalRelationTypeId: prev.mainHierarchicalRelationTypeId === state.originalId ? nextId : prev.mainHierarchicalRelationTypeId,
         relationTypes: renameRecord(prev.relationTypes, state.originalId, nextId, state.value)
      }));
      setEditRelationType(undefined);
   };

   const deleteConfirmed = () => {
      if (!deleteState) return;
      setProject((prev) => {
         if (deleteState.kind === "elementType") return { ...prev, elementTypes: removeRecord(prev.elementTypes, deleteState.elementTypeId) };
         if (deleteState.kind === "relationType") return { ...prev, mainHierarchicalRelationTypeId: prev.mainHierarchicalRelationTypeId === deleteState.relationTypeId ? undefined : prev.mainHierarchicalRelationTypeId, relationTypes: removeRecord(prev.relationTypes, deleteState.relationTypeId) };
         const elementType = prev.elementTypes[deleteState.elementTypeId];
         if (deleteState.kind === "attribute") {
            return { ...prev, elementTypes: updateRecord(prev.elementTypes, deleteState.elementTypeId, { ...elementType, attributes: removeRecord(elementType.attributes, deleteState.attributeId) }) };
         }
         const attribute = elementType.attributes[deleteState.attributeId];
         return { ...prev, elementTypes: updateRecord(prev.elementTypes, deleteState.elementTypeId, { ...elementType, attributes: updateRecord(elementType.attributes, deleteState.attributeId, { ...attribute, alerts: removeRecord(attribute.alerts || {}, deleteState.alertId) }) }) };
      });
      setDeleteState(undefined);
   };

   return (
      <AdvancedDragModal title={props.title || Localization.getText("PLA Project Editor")} PosDefault={{ width: "1000px", left: "50%", transform: "translateX(-50%)", top: "60px" }} onClose={props.onClose}>
         <ADModalBody>
            <GlassInputGroup onSubmit={() => props.onChange(project)} onEscape={props.onClose}>
               <GlassTextInputTransformer label={Localization.getText("Project name")}><input value={project.name} onChange={(event) => setProject({ ...project, name: event.target.value })} /></GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Project Description")}><textarea value={project.description} onChange={(event) => setProject({ ...project, description: event.target.value })} /></GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Main hierarchical relation")}>
                  <select value={selectedMainHierarchicalRelationTypeId} onChange={(event) => setProject({ ...project, mainHierarchicalRelationTypeId: event.target.value })}>
                     <option value="">{Localization.getText("No main hierarchical relation")}</option>
                     {sortedRelationTypes.map(([relationTypeId, relationType]) => <option key={relationTypeId} value={relationTypeId}>{relationType.name || relationTypeId}</option>)}
                  </select>
               </GlassTextInputTransformer>
               <GlassInputGroupTitle>{Localization.getText("Access Rights")}</GlassInputGroupTitle>
               <AccessRightsSubForm
                  subFormData={transformAccessRights(project.allowedUsersWithRoles)}
                  setSubFormData={(value) => setProject((prev) => ({ ...prev, allowedUsersWithRoles: accessRightsFromSubForm(value) }))}
               />
               <SectionHeaderRow title={Localization.getText("Element types")}>
                  <GlassButton onClick={openNewElementType}>{Localization.getText("Add element type")}</GlassButton>
               </SectionHeaderRow>
               <TinyTable>
                  <thead><tr><HeaderCell>{Localization.getText("Name")}</HeaderCell><HeaderCell>{Localization.getText("Color")}</HeaderCell><HeaderCell>{Localization.getText("Sort order")}</HeaderCell><HeaderCell>{Localization.getText("Attributes")}</HeaderCell><HeaderCell>{Localization.getText("Actions")}</HeaderCell></tr></thead>
                  <tbody>
                     {sortedElementTypes.map(([elementTypeId, elementType]) => {
                        const elementTypeIssues = expressionIssues.filter((issue) => issue.elementTypeId === elementTypeId);
                        return <tr key={elementTypeId} {...expressionIssueRowProps(project, elementTypeIssues)}>
                           <Cell>{elementType.name}</Cell>
                           <Cell><span style={{ display: "inline-block", width: 18, height: 18, borderRadius: 3, background: elementType.color || "#2d8f85", border: "1px solid rgba(255,255,255,.45)", verticalAlign: "middle" }} /></Cell>
                           <Cell>{elementType.sortOrder}</Cell>
                           <Cell>{Object.keys(elementType.attributes || {}).length}</Cell>
                           <Cell><GlassButtonRow><ActionIconButton title={Localization.getText("Edit element type")} icon="edit" onClick={() => setEditElementType({ originalId: elementTypeId, draftId: elementTypeId, value: elementType })} /><ActionIconButton title={Localization.getText("Delete element type")} icon="delete" onClick={() => setDeleteState({ kind: "elementType", elementTypeId })} /></GlassButtonRow></Cell>
                        </tr>;
                     })}
                  </tbody>
               </TinyTable>
               <SectionHeaderRow title={Localization.getText("Relation types")}>
                  <GlassButton onClick={openNewRelationType}>{Localization.getText("Add relation type")}</GlassButton>
               </SectionHeaderRow>
               <TinyTable>
                  <thead><tr><HeaderCell>{Localization.getText("Name")}</HeaderCell><HeaderCell>{Localization.getText("Actions")}</HeaderCell></tr></thead>
                  <tbody>
                     {sortedRelationTypes.map(([relationTypeId, relationType]) => (
                        <tr key={relationTypeId}>
                           <Cell>{relationType.name}</Cell>
                           <Cell><GlassButtonRow><ActionIconButton title={Localization.getText("Edit relation type")} icon="edit" onClick={() => setEditRelationType({ originalId: relationTypeId, draftId: relationTypeId, value: relationType })} /><ActionIconButton title={Localization.getText("Delete relation type")} icon="delete" onClick={() => setDeleteState({ kind: "relationType", relationTypeId })} /></GlassButtonRow></Cell>
                        </tr>
                     ))}
                  </tbody>
               </TinyTable>
            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow>
               <GlassButton onClick={() => props.onChange(project)}>{Localization.getText("Save")}</GlassButton>
               <GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
         {editElementType ? createPortal(<ElementTypeEditor project={project} state={editElementType} onClose={() => setEditElementType(undefined)} onSave={saveElementType} />, overlayRoot()) : null}
         {editRelationType ? createPortal(<RelationTypeEditor state={editRelationType} onClose={() => setEditRelationType(undefined)} onSave={saveRelationType} />, overlayRoot()) : null}
         {deleteState ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Confirm deletion")} onSubmit={deleteConfirmed} onCancel={() => setDeleteState(undefined)}>
               {Localization.getText("Confirm deletion")}
            </ConfirmationDialog>,
            overlayRoot()
         ) : null}
      </AdvancedDragModal>
   );
}
